import { useEffect } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  ClipboardIcon,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../components/ui/use-toast";
import {
  useGetSwapQuery,
  SwapStatus,
  useGetOrganizationsQuery,
} from "../generated/graphql";
import { generateRecipt } from "../lib/generateReceipt";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../components/ui/tooltip";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../AuthProvider";
import { formatDate } from "../lib/formatDate";
import { formatNumberToString } from "../lib/formatNumbers";
export function Swap() {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { toast } = useToast();
  const { id } = useParams();

  const [{ data: organizationData, error: organizationError }] =
    useGetOrganizationsQuery({});
  const organizationName = organizationData?.getOrganizations.find(
    (org) => org.id === organizationId
  )?.name;

  const [{ data, error, fetching }, refetchSwap] = useGetSwapQuery({
    variables: {
      swapId: id ? id : null || "",
    },
    pause: !id,
  });
  const swap = data?.getSwap;

  useEffect(() => {
    if (error) {
      console.error(error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
    if (organizationError) {
      console.error(organizationError);
      toast({
        title: "Error",
        description: organizationError.message,
        variant: "destructive",
      });
    }
  }, [error, toast, organizationError]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      refetchSwap({ requestPolicy: "network-only" });
    }, 10 * 1000);

    return () => clearTimeout(timerId);
  }, [refetchSwap, toast]);

  const steps = [
    { label: "Initiated", action: "Initiated" },
    { label: "Execute Swap", action: "ExecuteSwap" },
    { label: "Transfer Main", action: "TransferToMain" },
    { label: "Transfer Client", action: "TransferToClient" },
    { label: "Complete", action: "Complete" },
  ];

  const getCurrentStep = () => {
    if (!swap) return 0;
    if (swap.status === "Completed") return steps.length;

    if (!swap.steps || swap.steps.length === 0) return 0;

    const completedActions = new Set(
      swap.steps
        .filter((step) => step.status === "Completed")
        .map((step) => step.action)
    );

    for (let i = steps.length - 2; i >= 0; i--) {
      if (completedActions.has(steps[i].action)) {
        return i + 1;
      }
    }

    return 0;
  };

  const currentStep = getCurrentStep();
  const progress = (currentStep / (steps.length - 1)) * 100;

  return (
    <div className="flex min-h-screen w-full flex-col bg-gray-50">
      <Navbar />

      <header className="sticky top-0 z-10 flex h-16 items-center gap-2 px-2 md:px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/history")}
          className="mr-1 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg md:text-xl font-semibold text-gray-800 truncate">
          Swaps
        </h1>
      </header>

      <main className="flex-1 p-2 md:p-6 lg:p-8 flex flex-col">
        {/* Radix UI Progress Stepper */}
        <div className="max-w-full mx-auto py-2 md:py-4 mb-4 md:mb-10">
          {!fetching && (
            <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          <div className="flex justify-between mt-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center relative flex-1"
              >
                <div className="mb-2">
                  {index < currentStep ? (
                    <CheckCircle className="h-4 w-4 text-blue-500" />
                  ) : index === currentStep ? (
                    <Circle className="h-4 w-4 text-blue-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-300" />
                  )}
                </div>
                <span className="text-[10px] md:text-xs text-gray-600 text-center w-full">
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {swap != null ? (
          <div className="w-full max-w-4xl mx-auto space-y-4 md:space-y-6">
            <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
              <Card className="overflow-hidden">
                <CardHeader className="p-4">
                  <CardTitle className="text-lg">Swap Information</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">ID:</span>
                    <span>{swap.id}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status:</span>
                    <div className="flex items-center">
                      <div
                        className={`h-3 w-3 rounded-full mr-2 ${
                          swap.status === SwapStatus.Completed
                            ? "bg-green-500"
                            : swap.status === SwapStatus.Failed
                            ? "bg-red-500"
                            : swap.status === SwapStatus.Pending
                            ? "bg-yellow-500"
                            : "bg-gray-500"
                        }`}
                      />
                      <span>{swap.status}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 hover:bg-gray-50"
                    disabled={swap.toAmount == null}
                    onClick={() =>
                      generateRecipt({
                        organizationName: organizationName ?? "",
                        fromMint: swap.fromMint.name,
                        fromAmount: formatNumberToString(swap.fromAmount) ?? "",
                        toMint: swap.toMint.name,
                        toAmount: formatNumberToString(swap.toAmount) ?? "",
                        date: formatDate(swap.date, "DD-MM-YYYY HH:mm"),
                        swapId: swap.id,
                        txHash: swap.txHash ?? "",
                      })
                    }
                  >
                    Generate Receipt
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Currency Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">From Currency:</span>
                    <span className="flex items-center">
                      {swap.fromMint.name}
                      {swap.fromMint.img && (
                        <img
                          src={swap.fromMint.img}
                          alt={swap.fromMint.name}
                          className="inline-block h-4 w-4 ml-1"
                        />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">From Amount:</span>
                    <span>
                      {formatNumberToString(swap.fromAmount) ?? "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">To Currency:</span>
                    <span className="flex items-center">
                      {swap.toMint.name}
                      {swap.toMint.img && (
                        <img
                          src={swap.toMint.img}
                          alt={swap.toMint.name}
                          className="inline-block h-4 w-4 ml-1"
                        />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">To Amount:</span>
                    <span>{swap.toAmount ?? "N/A"}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Additional Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Created At:</span>
                    <span>{new Date(swap.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between flex-col">
                    <span className="font-medium mb-2">Withdrawl Address:</span>

                    <div className="flex justify-start gap-10 items-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              onClick={() => {
                                navigator?.clipboard?.writeText(
                                  swap.endAddress ?? ""
                                );
                                toast({
                                  title: "Copied to clipboard",
                                  description: "Address copied to clipboard",
                                  variant: "default",
                                });
                              }}
                              className="w-full"
                            >
                              <span className="truncate flex-1 text-left">
                                {swap.endAddress}
                              </span>
                              <ClipboardIcon className="ml-2 h-4 w-4 flex-shrink-0" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Click to copy</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Transaction Hash</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    disabled={!swap.txHash}
                    variant="outline"
                    className="w-full text-left flex items-center justify-between"
                    onClick={() => {
                      const explorerUrl =
                        swap.toMint.chain === "BTC"
                          ? process.env.REACT_APP_BTC_EXPLORER_URL
                          : process.env.REACT_APP_ETH_EXPLORER_URL;
                      window.open(`${explorerUrl}${swap.txHash}`, "_blank");
                    }}
                  >
                    {swap.txHash ? (
                      <>
                        <span className="truncate flex-1">{swap.txHash}</span>
                        <ExternalLink className="h-4 w-4 flex-shrink-0 ml-2" />
                      </>
                    ) : (
                      <span className="truncate flex-1">
                        No transaction hash yet.
                      </span>
                    )}
                  </Button>
                  <div className="flex justify-between">
                    {(swap.toMint.name === "USDC" ||
                      swap.toMint.name === "USDT") &&
                    (swap.fromMint.name === "USDC" ||
                      swap.fromMint.name === "USDT") ? (
                      <>
                        <span className="font-medium">BPS:</span>
                        <span>
                          {Math.round(
                            ((Number(swap.fromAmount) - Number(swap.toAmount)) /
                              Number(swap.fromAmount)) *
                              10000
                          )}
                        </span>
                      </>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-full">
            <p className="text-base md:text-lg text-gray-500">
              Loading swap details...
            </p>
          </div>
        )}
      </main>
      <Toaster />
    </div>
  );
}
